import React, { Component } from "react";
import { connect } from "react-redux";
import withStyles from "@material-ui/core/styles/withStyles";
import { GlobalState } from "../../types/globalState";
import { ChevronRight, GolfCourse } from "@material-ui/icons";
import Moment from "moment"; // Import Moment
import {
  Avatar,
  Badge,
  createStyles,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  Theme,
  WithStyles,
} from "@material-ui/core";
import { Game, GState } from "../../types/game";
import GameActions from "../../actions/games";
import "../../styles/material-list-responsive.css";
import GameNavigationDialog from "./GameNavigationDialog";
import { showDialog } from "../SharedComponents/Dialog";
import { User } from "../../types/user";
import { GameDetails } from "./GameDetails";
import ChainIcon from "./ChainIcon";
import { isRangeGame } from "../../utils/rhiGame";
import { rangeGameListItemStyle } from "./rangeGameStyles";

interface Props extends WithStyles<typeof styles> {
  // Ensure styles is typed if passed to withStyles
  getJoinedGames(): void;
  getCurrentOrganisedGames(): void;
  clearJoinedGames(): void;
  games: Game[];
  loading: boolean;
  user: User;
}

interface State {
  openNavigationMenu: boolean; // This state property is declared but not used. Consider removing if not needed.
}

// Helper function to sort games, grouping linked games together
function sortGamesAndGroupLinks(games: Game[]): Game[] {
  const gamesToSort = [...games]; // Operate on a copy
  const result: Game[] = [];
  const processedGameIds = new Set<string>(); // Keep track of games already added to the result

  // Initial sort by date descending to determine the order of processing groups
  gamesToSort.sort((a, b) => Moment(b.date).valueOf() - Moment(a.date).valueOf());

  for (const game of gamesToSort) {
    if (processedGameIds.has(game._id)) {
      continue; // Skip if already processed as part of a group
    }

    const currentGroup: Game[] = [];
    const queue: Game[] = [game]; // Start a queue for BFS-like traversal
    processedGameIds.add(game._id);
    let head = 0;

    while (head < queue.length) {
      const currentGameInQueue = queue[head++];
      currentGroup.push(currentGameInQueue);

      // Find games linked BY currentGameInQueue
      for (const linkedGameId of currentGameInQueue.linkedGames || []) {
        const linkedGame = gamesToSort.find(g => g._id === linkedGameId);
        if (linkedGame && !processedGameIds.has(linkedGame._id)) {
          processedGameIds.add(linkedGame._id);
          queue.push(linkedGame);
        }
      }

      // Find games that link TO currentGameInQueue (handles cases where links might not be perfectly symmetrical in data)
      // This also helps ensure all members of a connected component are found.
      for (const otherGame of gamesToSort) {
        if (!processedGameIds.has(otherGame._id) && (otherGame.linkedGames || []).includes(currentGameInQueue._id)) {
          processedGameIds.add(otherGame._id);
          queue.push(otherGame);
        }
      }
    }

    // Sort the discovered group internally by date (descending)
    currentGroup.sort((a, b) => Moment(b.date).valueOf() - Moment(a.date).valueOf());
    result.push(...currentGroup);
  }

  return result;
}

class JoinedGamesList extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      openNavigationMenu: false, // As noted, this state isn't used.
    };
    // Data fetching calls
    this.props.clearJoinedGames();
    this.props.getJoinedGames();
    this.props.getCurrentOrganisedGames();
  }

  renderAvatar(game: Game) {
    // Check if the current user is one of the organizers
    const isOrganizer = game.organizers.some(o => o._id === this.props.user._id);

    const avatarStyle = {
      backgroundColor: game.state === GState.Playing ? "#33a136" : "#8f8b8f",
    };

    const avatar = (
      <Avatar style={avatarStyle}>
        <GolfCourse />
      </Avatar>
    );

    if (isOrganizer) {
      return (
        <Badge badgeContent={"Org"} color="primary" anchorOrigin={{ vertical: "top", horizontal: "left" }}>
          {avatar}
        </Badge>
      );
    }
    return avatar;
  }

  renderItem(game: Game, index: number, gamesList: Game[]) {
    // Renamed games to gamesList for clarity
    // Basic validation for the game object
    if (typeof game !== "object" || !game || !game._id) return null;

    return (
      // Using game._id as key for the outer div
      <div key={game._id} style={{ width: "100%", position: "relative" }}>
        <ListItem
          dense={false}
          button
          style={{
            alignItems: "center",
            ...rangeGameListItemStyle(isRangeGame(game)),
          }}
          onClick={() =>
            game._id &&
            showDialog(<GameNavigationDialog gameId={game._id} />, "xs", true, true, true, "Game menu")
          }
        >
          <ListItemAvatar className={this.props.classes.listAvatar}>{this.renderAvatar(game)}</ListItemAvatar>
          <div className={this.props.classes.cardDescContainer}>
            {/* GameDetails component to display game information */}
            <GameDetails key={`details-${game._id}`} game={game} showEntryFee />{" "}
            {/* Ensure GameDetails also has a key if it's part of a list internally */}
            <ChevronRight color={"primary"} /> {/* Navigation indicator */}
          </div>
        </ListItem>
        <Divider component="li" />
        {/* ChainIcon: currentGame is the current game, nextGame is the physically next game in the sorted list */}
        <ChainIcon currentGame={game} nextGame={gamesList[index + 1]} />
      </div>
    );
  }

  render() {
    const { classes, games, loading } = this.props;

    // Display message if no games and not loading
    if (!loading && games.length === 0) {
      return (
        <h4 className={"text-center medium-padding"}>You have no Current Games. Enter a game code to join a game.</h4>
      );
    }

    return (
      <List className={classes.paper}>
        {loading && <LinearProgress />} {/* Show LinearProgress if loading */}
        {/* The 'games' prop is now pre-sorted by sortGamesAndGroupLinks via mapStateToProps */}
        {games.map((game: Game, index: number, gamesList: Game[]) => this.renderItem(game, index, gamesList))}
      </List>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      backgroundColor: "rgba(255, 255, 255, 1)", // Ensuring the list has a background
      padding: 0, // Remove default padding from List if any, to make Dividers flush
    },
    container: {
      // This class is defined but not used in the component JSX.
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    },
    listAvatar: {
      alignSelf: "center",
      minWidth: 56,
      marginLeft: theme.spacing(0.5),
    },
    cardDescContainer: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(1.5, 1, 1.5, 1),
      minWidth: 0,
    },
  });

const mapStateToProps = (state: GlobalState) => {
  const { joinedGames, organisedByMe } = state.games;
  const { loading } = state.general;
  const { user } = state.auth;

  // Combine organised and joined games into a single object, then get values
  const allGamesObject = { ...organisedByMe, ...joinedGames };
  const allGamesArray = Object.values(allGamesObject);

  // Apply the new sorting function that groups linked games
  const sortedGames = sortGamesAndGroupLinks(allGamesArray);

  return {
    user,
    loading,
    games: sortedGames, // Provide the specially sorted list of games to the component
  };
};

export default connect(mapStateToProps, {
  getJoinedGames: GameActions.getJoinedGames,
  getCurrentOrganisedGames: GameActions.getCurrentOrganisedGames,
  clearJoinedGames: GameActions.clearJoinedGames,
})(withStyles(styles)(JoinedGamesList));
